import { useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchXaiKey } from '@/lib/themeReference';
import { logAiUsage, readUsage } from '@/lib/aiUsageLog';
import { getBusinessCategory } from '@/lib/themeBusinessCategories';

// ============================================================
// Génération d'UNE seule page manquante d'un thème.
// La page générée s'appuie sur :
//   • la page d'accueil déjà générée (pour rester cohérente),
//   • le fichier de référence,
//   • les balises Zifek.
// Elle est enregistrée directement dans le thème (sitewebthemepage).
// ============================================================

interface PageDefLite {
  key: string;
  label: string;
  description?: string;
}

interface PageGeneratorModalProps {
  themeId: number;
  pageDefs: PageDefLite[];
  existingKeys: string[];
  referenceContent: string;
  businessCategory: string;
  homeContent: string;
  userId: number | null;
  defaultKey?: string;
  onClose: () => void;
  onDone: () => void;
}

const XAI_ENDPOINT = 'https://api.x.ai/v1/chat/completions';
const XAI_MODEL = 'grok-4.6';

export default function PageGeneratorModal({
  themeId,
  pageDefs,
  existingKeys,
  referenceContent,
  businessCategory,
  homeContent,
  userId,
  defaultKey,
  onClose,
  onDone,
}: PageGeneratorModalProps) {
  const existing = useMemo(() => new Set(existingKeys), [existingKeys]);

  const [mode, setMode] = useState<'existing' | 'custom'>(() => (defaultKey ? 'existing' : 'custom'));
  const [key, setKey] = useState(defaultKey || '');
  const [customKey, setCustomKey] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [extra, setExtra] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const finalKey = (mode === 'existing' ? key : customKey).trim().toLowerCase().replace(/[^a-z0-9_-]/g, '-');  const finalLabel = mode === 'existing'
    ? (pageDefs.find((d) => d.key === key)?.label || key)
    : (customLabel.trim() || customKey.trim());

  const handleGenerate = async () => {
    if (!finalKey) { setError('Choisis ou saisis une page à générer.'); return; }
    setBusy(true);
    setError('');
    const startedAt = Date.now();
    try {
      const apiKey = await fetchXaiKey();
      if (!apiKey) throw new Error('Aucune clé API xAI configurée.');

      const system = `Tu es un Designer UI/UX Senior et développeur front expert pour le moteur de thèmes Zifek.
Tu génères UNE SEULE page HTML d'un thème, en respectant scrupuleusement le style et la structure de la page d'accueil fournie.

RÈGLES ABSOLUES
1. Réutilise EXACTEMENT les mêmes classes CSS et la même structure de sections que la page d'accueil fournie.
2. N'utilise QUE les balises Zifek : {{site_name}} {{site_description}} {{site_logo}} {{site_phone}} {{site_email}} {{site_address}} {{year}} ainsi que {{products}} {{services}} {{blog_posts}} {{faq}} {{gallery}} {{team}} {{partners}} {{portfolio}} {{testimonials}} {{contact_form}} {{booking_form}} {{marketplace_products}} {{marketplace_categories}} {{featured_products}} {{hebergements}} {{hebergement_search}} {{events}} {{categories}} {{restauration}} {{default_form}}.
3. PAS de JavaScript, PAS de CSS inline massif, PAS de texte fictif : uniquement la structure HTML + balises Zifek.
4. Réponds UNIQUEMENT par le code HTML de la page, sans balise <html>/<body>, sans explication.

${getBusinessCategory(businessCategory).promptBlock}

📚 FICHIER DE RÉFÉRENCE (extrait) :
${referenceContent.slice(0, 4000)}`;

      const user = `Génère la page « ${finalLabel} » (clé: ${finalKey}).
${extra.trim() ? `Consignes spécifiques : ${extra.trim()}` : ''}

Voici la page d'accueil de référence (respecte son style et ses sections) :
${homeContent.slice(0, 6000)}

Renvoyez uniquement le HTML de la page « ${finalKey} ».`;

      const res = await fetch(XAI_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify({
          model: XAI_MODEL,
          messages: [{ role: 'system', content: system }, { role: 'user', content: user }],
          temperature: 0.6,
          max_tokens: 4000,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Erreur IA ${res.status}: ${t.slice(0, 160)}`);
      }
      const data = await res.json();
      let html: string = data?.choices?.[0]?.message?.content || '';
      html = html.replace(/^```[a-zA-Z]*\s*\n?/, '').replace(/\n?```\s*$/, '').trim();
      if (!html) throw new Error('Réponse IA vide.');

      await logAiUsage({
        provider: 'xai', model: XAI_MODEL, endpoint: XAI_ENDPOINT,
        ...readUsage(data?.usage), durationMs: Date.now() - startedAt,
        status: 'success', userId,
      });

      // Enregistre (met à jour la page existante ou la crée).
      const { data: existingRow } = await supabase
        .from('sitewebthemepage')
        .select('id')
        .eq('idtheme', themeId)
        .eq('page_key', finalKey)
        .maybeSingle();

      if (existingRow?.id) {
        await supabase.from('sitewebthemepage')
          .update({ title: finalLabel, content: html, updated_at: new Date().toISOString() })
          .eq('id', existingRow.id);
      } else {
        await supabase.from('sitewebthemepage')
          .insert({ idtheme: themeId, page_key: finalKey, title: finalLabel, content: html });
      }

      onDone();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[70]" onClick={busy ? undefined : onClose} />
      <div className="fixed inset-0 z-[71] flex items-center justify-center p-4">
        <div className="w-full max-w-lg bg-background-50 border border-background-200/70 rounded-lg flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
            <span className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-magic-line text-accent-500"></i>Générer une page par IA
            </span>
            <button onClick={onClose} disabled={busy} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer disabled:opacity-40">
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5 w-fit">
              <button
                onClick={() => setMode('existing')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${mode === 'existing' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'}`}
              >
                Page du thème
              </button>
              <button
                onClick={() => setMode('custom')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${mode === 'custom' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500'}`}
              >
                Nouvelle page
              </button>
            </div>

            {mode === 'existing' ? (
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Page à (re)générer</label>
                <select value={key} onChange={(e) => setKey(e.target.value)} className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm cursor-pointer">
                  <option value="">— Choisir une page —</option>
                  {pageDefs.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}{existing.has(d.key) ? ' — existe déjà (sera remplacée)' : ' — à créer'}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-foreground-400 mt-1.5">
                  Seule cette page sera écrite. Les autres pages du thème ne sont jamais modifiées.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Clé de page</label>
                  <input value={customKey} onChange={(e) => setCustomKey(e.target.value)} placeholder="ex: tarifs" className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Libellé</label>
                  <input value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="ex: Page Tarifs" className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Consignes (optionnel)</label>
              <textarea value={extra} onChange={(e) => setExtra(e.target.value)} rows={2} placeholder="ex: reprends la section hero de l'accueil, ajoute une grille de tarifs" className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm resize-none" />
            </div>

            {error && (
              <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
                <span className="text-xs text-red-700">{error}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-5 h-14 border-t border-background-200/70">
            <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 cursor-pointer whitespace-nowrap disabled:opacity-40">Annuler</button>
            <button
              onClick={handleGenerate}
              disabled={busy || !finalKey}
              className="px-5 py-2 rounded-full text-sm font-semibold bg-foreground-950 text-background-50 whitespace-nowrap hover:bg-foreground-800 disabled:opacity-50 cursor-pointer flex items-center gap-2"
            >
              {busy ? <><i className="ri-loader-4-line animate-spin"></i>Génération…</> : <><i className="ri-magic-line"></i>Générer</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}